import { useCallback, useEffect, useRef, useState } from "react";
import { CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, PanelLabel } from "@/components/PageHeader";
import { MonthPicker } from "@/components/MonthPicker";
import { ScheduledFormDialog } from "@/components/scheduled/ScheduledFormDialog";
import { FilterTabs, ScheduledList, type Filter } from "@/components/scheduled/ScheduledList";
import { BiggestPayments, CardPayments, MonthCalendar, StatCards } from "@/components/scheduled/ScheduledSide";
import { api, formatApiError } from "@/lib/api";
import type { BudgetItemRow } from "@/lib/budget";
import type { Account, AccountsResponse, Category } from "@/lib/finance";
import type { Occurrence, Schedule, ScheduledResponse } from "@/lib/scheduled";
import { AheadDialog, ConfirmDialog, ScopeDialog, type Scope } from "@/components/scheduled/ScopeDialog";
import { moneyIn, rateText } from "@/lib/format";

interface PayPreview { amount: number; currency: string; account_name: string; account_currency: string; needs_confirm: boolean; converted?: number; rate?: number; rate_currency?: string }
import { currentMonth } from "@/lib/format";

export default function ScheduledPage() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState<ScheduledResponse | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [items, setItems] = useState<BudgetItemRow[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Schedule | null>(null);
  const [paying, setPaying] = useState<string | null>(null);
  const [target, setTarget] = useState<Occurrence | null>(null);
  const [ask, setAsk] = useState<"edit" | "delete" | null>(null);
  const [edit, setEdit] = useState<{ occ: Occurrence; scope: Scope } | null>(null);

  const load = useCallback(() => {
    api.get<ScheduledResponse>("/scheduled", { params: { month } }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [month]);
  useEffect(load, [load]);
  useEffect(() => {
    api.get<AccountsResponse>("/accounts").then((r) => setAccounts(r.data.accounts));
    api.get<Category[]>("/categories").then((r) => setCategories(r.data));
    api.get<BudgetItemRow[]>("/budget-items").then((r) => setItems(r.data));
  }, []);

  const [ahead, setAhead] = useState<Occurrence | null>(null);
  const [fxAsk, setFxAsk] = useState<{ o: Occurrence; info: PayPreview } | null>(null);
  const [undo, setUndo] = useState<Occurrence | null>(null);
  const busy = useRef(false);
  const pay = (o: Occurrence) => { if (o.is_oldest) checkFx(o); else setAhead(o); };
  const checkFx = async (o: Occurrence) => {
    if (busy.current) return;
    try {
      const r = await api.get<PayPreview>(`/scheduled/${o.schedule_id}/pay-preview`, { params: { occurrence: o.occurrence_date } });
      if (r.data.needs_confirm) setFxAsk({ o, info: r.data }); else payNow(o);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const payNow = async (o: Occurrence, fxRate?: number) => {
    if (busy.current) return;
    busy.current = true;
    const id = o.schedule_id;
    setPaying(id);
    try {
      const r = await api.post<{ finished: boolean; warning?: string | null }>(`/scheduled/${id}/pay`, null, { params: { occurrence: o.occurrence_date, fx_rate: fxRate } });
      toast.success(!o.is_oldest ? "Registrado por adelantado." : r.data.finished ? "Registrado. Este programado ya terminó." : "Registrado. Se movió a su siguiente fecha.");
      if (r.data.warning) toast.warning(r.data.warning, { duration: 8000 });
      load();
    } catch (e) { toast.error(formatApiError(e)); load(); }
    busy.current = false;
    setPaying(null);
  };
  const doUndo = async (o: Occurrence) => {
    try { await api.post(`/scheduled/payments/${o.transaction_id}/undo`); toast.success("Cobro deshecho: se borró el movimiento y el pago vuelve a estar pendiente"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const recurring = (o: Occurrence) => o.frequency !== "once";
  const doDelete = async (o: Occurrence, scope: Scope) => {
    try {
      await api.delete(`/scheduled/${o.schedule_id}`, { params: { scope, occurrence: o.occurrence_date } });
      toast.success(scope === "one" && recurring(o) ? "Se quitó solo este pago" : "Programado eliminado"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openEdit = (o: Occurrence, scope: Scope) => {
    setEditing(data?.schedules.find((s) => s.id === o.schedule_id) ?? null);
    setEdit({ occ: o, scope }); setOpen(true);
  };
  const remove = (o: Occurrence) => {
    if (recurring(o)) { setTarget(o); setAsk("delete"); return; }
    if (window.confirm("¿Eliminar este programado? Los movimientos ya registrados no se borran.")) doDelete(o, "all");
  };
  const startEdit = (o: Occurrence) => { if (recurring(o)) { setTarget(o); setAsk("edit"); } else openEdit(o, "all"); };
  const picked = (scope: Scope) => { const o = target!; const a = ask; setAsk(null); if (a === "delete") doDelete(o, scope); else openEdit(o, scope); };
  const duplicate = async (o: Occurrence) => {
    try { await api.post(`/scheduled/${o.schedule_id}/duplicate`); toast.success("Programado duplicado como una regla nueva"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const visible = (data?.occurrences ?? []).filter((o) => filter === "all" || (filter === "income" ? o.type === "income" : o.type !== "income"));

  return (
    <section data-testid="scheduled-page">
      <PageHeader title="Programados" testId="scheduled-title" actions={
        <Button onClick={() => { setEditing(null); setEdit(null); setOpen(true); }} className="h-11 bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="new-scheduled-button">
          <CalendarPlus className="mr-2 h-4 w-4" /> Programar movimiento
        </Button>
      } />
      <div className="mb-6"><MonthPicker month={month} onChange={setMonth} /></div>
      {data && (
        <div className="fade-up space-y-6">
          <StatCards s={data.summary} />
          <div className="grid gap-6 lg:grid-cols-[1.8fr_1fr]">
            <div className="space-y-6">
              <Panel testId="scheduled-panel">
                <div className="flex flex-wrap items-center justify-between gap-3"><PanelLabel>Por pagar y por cobrar</PanelLabel><FilterTabs value={filter} onChange={setFilter} /></div>
                <div className="mt-2"><ScheduledList items={visible} onPay={pay} onEdit={startEdit} onDelete={remove} onDuplicate={duplicate} onUndo={setUndo} paying={paying} /></div>
              </Panel>
              <CardPayments items={data.card_payments} />
            </div>
            <div className="space-y-6">
              <MonthCalendar month={data.month} today={data.today} items={data.occurrences} />
              <BiggestPayments items={data.biggest} />
            </div>
          </div>
        </div>
      )}
      <ScheduledFormDialog open={open} onOpenChange={setOpen} accounts={accounts} categories={categories} items={items} initial={editing} occurrence={edit?.occ ?? null} scope={edit?.scope ?? "all"} onSaved={load} />
      <ScopeDialog action={ask} onPick={picked} onClose={() => setAsk(null)} />
      <AheadDialog o={ahead} onClose={() => setAhead(null)} onConfirm={() => { const o = ahead!; setAhead(null); checkFx(o); }} />
      <ConfirmDialog open={!!fxAsk} testId="scheduled-fx-dialog" onClose={() => setFxAsk(null)} confirm="Sí, registrarlo"
        title={fxAsk ? `${fxAsk.info.account_name} está en ${fxAsk.info.account_currency}` : ""}
        text={fxAsk ? `El programado es de ${moneyIn(fxAsk.info.amount, fxAsk.info.currency)}. Se registrará como ${moneyIn(fxAsk.info.converted ?? 0, fxAsk.info.account_currency)} con un tipo de cambio de ${rateText(fxAsk.info.rate ?? 0)} por ${moneyIn(1, fxAsk.info.rate_currency ?? "USD")}.` : ""}
        onConfirm={() => { const x = fxAsk!; setFxAsk(null); payNow(x.o, x.info.rate ?? undefined); }} />
      <ConfirmDialog open={!!undo} testId="scheduled-undo-dialog" onClose={() => setUndo(null)} confirm="Sí, deshacer"
        title={undo?.type === "income" ? "¿Deshacer este cobro?" : "¿Deshacer este pago?"}
        text="Se borra el movimiento que se creó y el pago vuelve a quedar pendiente en su fecha."
        onConfirm={() => { const o = undo!; setUndo(null); doUndo(o); }} />
    </section>
  );
}
